import { is, List } from "immutable";
import * as moment from "moment";
import * as QueryString from "querystringify";
import * as React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import { AuthorshipForm, ComposeStep, DetailsForm, IComposeStep, ProgressSteps, ReviewForm, UploadsForm } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { AuthHelper, Utility } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    loadingTypes: state.abstract.loadingEventType,
    mediaProcessing: state.abstract.mediaProcessing,
    step: state.router.location && typeof state.router.location.state === "string"
        ? state.router.location.state as ComposeStep
        : undefined,
    types: state.abstract.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadTypes: (eventId: number) => dispatch(Actions.Abstract.loadTypes(eventId)),
    subscribeToMediaProcessing: (mediaId: number) => dispatch(Actions.DBSubs.subscribeToMediaProcessing(mediaId)),
    toggleLoadTypes: (eventId: number, loading: boolean) => dispatch(Actions.Abstract.toggleLoadingEventType(eventId, loading)),
});

type OwnProps = RouteComponentProps<{
    eventId: string;
    guid: string;
    step: string;
}> & {
};

type ConnectedState = Comp.Base.DefaultConnectedState & {
    loadingTypes: M.Generic.ItemLoadingState;
    mediaProcessing: M.Generic.ItemLoadingState;
    step?: ComposeStep;
    types: List<M.Abstract.AbstractType>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadTypes: (eventId: number) => void;
    subscribeToMediaProcessing: (mediaId: number) => void;
    toggleLoadTypes: (eventId: number, loading: boolean) => void;
};

interface OwnState {
    abstract?: M.Abstract.Abstract;
    duesAmount?: M.Member.DuesAmount;
    loadingAbstract: boolean;
    loadingType: boolean;
    type?: M.Abstract.AbstractType;
    types: List<M.Abstract.AbstractType>;
    userStats?: M.Abstract.UserStats;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

// interface QueryParams {
//     step?: ComposeStep;
// }

class ComposerComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _composerForm: IComposeStep | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
            loadingAbstract: Utility.isGuid(this.getGuid()),
            loadingType: Utility.isGuid(this.getGuid()),
            types: List(),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        this.props.updateChildOfPath(Paths.User.Abstracts.UserAbstracts);
        const eventId = this.getEventId();
        const guid = this.getGuid();

        if (isNaN(eventId) || eventId <= 0) {
            this.props.notifyError("Invalid event identifier");
            this.props.goto(Paths.Home);
        }

        if (!(Utility.isGuid(guid) || guid === "new")) {
            this.props.notifyError("Invalid abstract identifier in URL");
            this.props.replace(Paths.User.Abstracts.UserAbstracts);
            return;
        }

        let urlStep = this.props.match.params.step as ComposeStep;
        if (!urlStep) {
            this.updateStep("authorship", true);
            urlStep = "authorship";
        }
        else if (guid === "new" && urlStep !== "authorship") {
            this.props.replace(Paths.AbstractsComposeNew(eventId));
            urlStep = "authorship";
        }

        const isUploadStep = urlStep === "uploads";
        const stateStep = this.props.step;
        if (!stateStep && urlStep)
            this.updateStep(urlStep, true);

        this.loadDataFromState();

        // check abstract settings
        const settings = await M.Abstract.getAbstractSettings(this.props.api, eventId);
        if (!settings) {
            this.props.notifyError(`Abstract submissions are not enabled for event ${eventId}`);
            this.props.goto(Paths.User.Abstracts.UserAbstracts);
            return;
        }

        // make sure submissions are enabled
        if (!isUploadStep && !M.Abstract.isSubmissionsOpen(settings)) {
            const submissionsOpenOn = moment(Utility.utcToLocal(settings.submissionsOpenOn));
            this.props.notifyError(`Abstract submissions for ${settings.eventName} open on ${submissionsOpenOn.format("MMM D, YYYY")}`);
            this.props.goto(Paths.User.Abstracts.UserAbstracts);
            return;
        }

        // make sure user has an active Member record
        // const statusIsActive = await M.Abstract.memberStatusIsAllowed(this.props.api, this.props.tokenData.userId || 0);
        // if (!statusIsActive) {
        //     this.props.notifyInfo("Our records indicate your membership status is not active.");
        //     this.props.goto(Paths.User.Abstracts.UserAbstracts);
        //     return;
        // }

        if (Utility.isGuid(guid)) {
            const userId = this.props.tokenData.userId || 0;
            const hasAccess = await M.Abstract.userHasAccessToAbstract(this.props.api, userId, guid);
            if (!hasAccess) {
                this.props.notifyError("You do not have access to the requested abstract.");
                this.props.goto(Paths.User.Abstracts.UserAbstracts);
                return;
            }
        }

        this.loadData();

        // display amount due if any
        setTimeout(async () => {
            const amountDueResult = await M.Member.MemberHelper.getAmountDue(this.props.api, this.props.tokenData.userId || 0);
            if (amountDueResult.isSuccess && amountDueResult.data && amountDueResult.data.amount < 0)
                await this.setStateAsync({ ...this.state, duesAmount: amountDueResult.data });
        }, 10);
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>, prevState: Readonly<OwnState>) {
        if (!is(prevProps.types, this.props.types))
            this.loadDataFromState();

        const urlStep = this.getComposeStep();
        const stateStep = this.props.step;
        if (!stateStep && urlStep) {
            console.log("steps are different (", stateStep, "/=", urlStep, ") update to", urlStep);
            this.updateStep(urlStep);
        }

        // a media has finished processing, so update it
        if (!is(prevProps.mediaProcessing, this.props.mediaProcessing) && this.state.abstract)
            this.updateProcessedMedia(this.state.abstract);

        // make sure abstract type's deadlines are not passed yet to allow for edits
        if (!is(prevState.type, this.state.type) || (prevProps.step !== this.props.step && this.props.step))
            this.checkTypeDeadlines();
    }

    public componentWillUnmount() {
        this._isMounted = false;
        this.props.updateChildOfPath();
    }

    public render() {
        const eventId = this.getEventId();
        const loadingTypes = !!this.props.loadingTypes[eventId];
        const duesAmount = (this.state.duesAmount && this.state.duesAmount.amount) || 0;

        const step = this.props.step || "authorship";
        let currentForm: JSX.Element | null = null;
        const isNew = this.getGuid() === "new";

        switch (step) {
            case "authorship":
                currentForm = <AuthorshipForm
                    abstract={this.state.abstract}
                    api={this.props.api}
                    countries={this.props.countries}
                    eventId={eventId}
                    preSelectedFormat={(QueryString.parse(this.props.location.search) as any).format}
                    type={this.state.type}
                    types={this.state.types}
                    submittingUser={AuthHelper.getBasicUserProfile(this.props.tokenData)}
                    userStats={this.state.userStats}
                    onAbstractSaved={(abstract, isSubmitLater) => this.onAbstractSavedHandler(abstract, isSubmitLater)}
                    onForceAuthRefresh={() => this.props.forceAuthRefresh()}
                    onBack={() => this.gotoPreviousStep()}
                    onSkip={() => this.gotoNextStep()}
                    ref={(ref) => this._composerForm = ref}
                />;
                break;
            case "details":
                currentForm = <DetailsForm
                abstract={this.state.abstract}
                    api={this.props.api}
                    eventId={eventId}
                    type={this.state.type}
                    onAbstractSaved={(abstract, isSubmitLater) => this.onAbstractSavedHandler(abstract, isSubmitLater)}
                    onBack={() => this.gotoPreviousStep()}
                    onSkip={() => this.gotoNextStep()}
                    ref={(ref) => this._composerForm = ref}
                />;
                break;
            case "uploads":
                currentForm = <UploadsForm
                abstract={this.state.abstract}
                    api={this.props.api}
                    eventId={eventId}
                    type={this.state.type}
                    onAbstractSaved={(abstract, isSubmitLater) => this.onAbstractSavedHandler(abstract, isSubmitLater)}
                    onBack={() => this.gotoPreviousStep()}
                    onSkip={() => this.gotoNextStep()}
                    onMediaDeleted={(abstractMediaId) => this.handleAbstractMediaDelete(abstractMediaId)}
                    subscribeToMediaProcessing={this.props.subscribeToMediaProcessing}
                    updateAbstractMedia={(media) => this.updateAbstractMedia(media)}
                    ref={(ref) => this._composerForm = ref}
                />;
                break;
            case "review":
                currentForm = <ReviewForm
                abstract={this.state.abstract}
                    api={this.props.api}
                    eventId={eventId}
                    type={this.state.type}
                    onAbstractSaved={(abstract, isSubmitLater) => this.onAbstractSavedHandler(abstract, isSubmitLater)}
                    onBack={() => this.gotoPreviousStep()}
                    onSkip={() => this.gotoNextStep()}
                    ref={(ref) => this._composerForm = ref}
                />;
                break;
            default:
                currentForm = null;
        }

        const abstractTypeTitle = this.state.type
            ? <h3>{this.state.type.name}</h3>
            : null;

        const abstractTitle = this.state.abstract
            ? <h1 dangerouslySetInnerHTML={{ __html: this.state.abstract.title }} />
            : null;

        return <Comp.Page.PublicPage additionalClasses="full" pageTitle="Abstract submission" style={{ position: "relative" }}>
            <Comp.Generic.LoadingSpinner show={loadingTypes || this.state.loadingAbstract || this.state.loadingType} />

            <Comp.Layout.ContentPrimary>

                {this.getErrorAlert()}

                {abstractTitle}
                {abstractTypeTitle}

                <BS.Collapse in={duesAmount < 0}>
                    <BS.Alert bsStyle="info">
                        <h3><Comp.Generic.Icon icon="info-circle" /> Please renew your membership</h3>
                        <p>
                            You have an unpaid account balance of ${Utility.formatThousands(-duesAmount)}.{" "}
                            To submit an abstract as a member please <a href={Paths.Legacy.MakePayment}>renew your membership</a>.
                        </p>
                    </BS.Alert>
                </BS.Collapse>

                <ProgressSteps
                    abstractGuid={this.state.abstract ? this.state.abstract.guid : undefined}
                    accessDetails={this.state.abstract !== undefined && this.state.abstract.editsAllowed}
                    accessUploads={this.state.abstract !== undefined && this.state.abstract.uploadsAllowed}
                    currentStep={step || "authorship"}
                    eventId={eventId}
                    hasDetails={isNew || !Utility.isNullOrUndefined(this.state.type && this.state.type.form)}
                    hasUploads={isNew || (this.state.type !== undefined && this.state.type.typeMedia.length > 0)}
                    onClick={(nextStep: ComposeStep) => this.updateStep(nextStep, false)}
                 />

                {currentForm}
            </Comp.Layout.ContentPrimary>

            <Comp.Layout.ContentSecondary>

            </Comp.Layout.ContentSecondary>
            <div className="clear"></div>
        </Comp.Page.PublicPage>;
    }

    private checkTypeDeadlines() {
        if (this.state.type) {
            const type = this.state.type;
            const step = this.getComposeStep();
            const deadlineStr = step === "uploads" && type.uploadDeadline
                ? type.uploadDeadline
                : type.submitDeadline;

            const deadline = moment(Utility.utcToLocal(deadlineStr));
            if (deadline.diff(moment(), "hours", true) < 0) {
                this.props.notifyError(`Abstract ${step === "uploads" ? "uploads" : "submissions"} for ${type.name} have closed ${deadline.format("MMM D, YYYY")}`);
                this.props.goto(Paths.User.Abstracts.UserAbstracts);
                return;
            }
        }
    }

    private getComposeStep(props?: CombinedProps): ComposeStep {
        // const query = this.getQueryParams(props);
        return props
            ? props.step as ComposeStep
            : this.props.step as ComposeStep;
    }

    private getEventId() {
        return parseInt(this.props.match.params.eventId, 10) || 0;
    }

    private getGuid() {
        return this.props.match.params.guid;
    }

    private getPreviousStep(currentStep: ComposeStep): ComposeStep | undefined {
        const typeHasDetails = this.state.type && !Utility.isNullOrUndefined(this.state.type.form);
        const typeHasUploads = !!this.state.type && this.state.type.typeMedia.length;

        switch (currentStep) {
            case "authorship":
                return undefined;
            case "details":
                return "authorship";
            case "uploads":
                return typeHasDetails ? "details" : "authorship";
            case "review":
                return typeHasUploads ? "uploads" : (typeHasDetails ? "details" : "authorship");
            default:
                return undefined;
        }
    }

    private getNextStep(currentStep: ComposeStep): ComposeStep | undefined {
        const typeHasDetails = this.state.type && !Utility.isNullOrUndefined(this.state.type.form);
        const typeHasUploads = !!this.state.type && this.state.type.typeMedia.length;

        switch (currentStep) {
            case "authorship":
                return typeHasDetails ? "details" : (typeHasUploads ? "uploads" : "review");
            case "details":
                return typeHasUploads ? "uploads" : "review";
            case "uploads":
                return "review";
            case "review":
            default:
                return undefined;
        }
    }

    private handleAbstractMediaDelete(abstractMediaId: number) {
        const abstract = this.state.abstract;
        if (abstract) {
            let allMedia = abstract ? List(abstract.media) : List([]);
            const idx = allMedia.findIndex((m: M.Abstract.AbstractMedia) => m.abstractMediaId === abstractMediaId);

            if (idx > -1) {
                allMedia = allMedia.remove(idx);
                abstract.media = allMedia.toArray();
                this.setState({ ...this.state, abstract });
            }
        }
    }

    private async listenForMediaNotifications(abstract: M.Abstract.Abstract) {
        for (const m of abstract.media.filter((x) => x.isProcessing)) {
            this.props.subscribeToMediaProcessing(m.id);
            await Utility.delay(125);
        }
    }

    private loadDataFromState() {
        const eventId = this.getEventId();

        // get types from state
        const types = this.props.types.filter((x: M.Abstract.AbstractType) => x.eventId === eventId).toList();
        if (types.size)
            this.setState({ ...this.state, types });
        else
            this.props.toggleLoadTypes(eventId, true);
    }

    private loadData() {
        const eventId = this.getEventId();
        this.props.loadTypes(eventId);

        const guid = this.getGuid();
        if (Utility.isGuid(guid)) {
            this.loadAbstract(guid);
            this.loadAbstractType(guid);
        }

        this.loadUserStats(eventId);
    }

    private async loadAbstract(guid: string) {
        const result = await this.props.api.get(Api.endpoints.abstract.abstract.withUrlParams({ guid }));
        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            const abstract = result.data;

            // check edit access
            if (this.props.step === "uploads" && !abstract.uploadsAllowed) {
                this.props.notifyInfo("Uploads have been disabled");
                this.props.goto(Paths.User.Abstracts.UserAbstracts);
                return;
            }
            else if (this.props.step !== "uploads" && !abstract.editsAllowed) {
                this.props.notifyInfo("Edits have been disabled");
                this.props.goto(Paths.User.Abstracts.UserAbstracts);
                return;
            }

            this.setState({ ...this.state, abstract, loadingAbstract: false }, () => {
                if (this._composerForm && abstract)
                    this._composerForm.setFormData(abstract);
            });

            // subscribe to processing media notifications
            this.listenForMediaNotifications(abstract);
        }
        else {
            this.setState({ ...this.state, loadingAbstract: false }, () => {
                this.setErrors(M.Result.Result.errorsOrMessage(result), "Failed to load abstract");
            });
        }
    }

    private async loadAbstractType(guid: string) {
        const result = await this.props.api.get(Api.endpoints.abstract.typeByAbstractGuid.withUrlParams({ guid }));
        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            const type = result.data;

            if (this.getComposeStep() === "uploads" && M.Abstract.isUploadDeadlinePassed(type)) {
                const uploadDeadline = moment(Utility.utcToLocal(type.uploadDeadline!));
                this.props.notifyError(`The deadline for uploads was on ${uploadDeadline.format("MMM D, YYYY")}`);
                this.props.goto(Paths.User.Abstracts.UserAbstracts);
                return;
            }

            this.setState({ ...this.state, type, loadingType: false });
        }
        else {
            this.setState({ ...this.state, loadingType: false }, () => {
                this.setErrors(M.Result.Result.errorsOrMessage(result), "Failed to load abstract type");
            });
        }
    }

    private async loadAbstractMedia(id: number) {
        const result = await this.props.api.get(Api.endpoints.abstract.media.withUrlParams({ id }));
        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            const abstract = this.state.abstract;
            if (abstract) {
                const allMedia = abstract ? List(abstract.media) : List([]);
                const media = allMedia.find((m: M.Abstract.AbstractMedia) => m.abstractMediaId === id);
                if (media) {
                    media.isProcessing = result.data.isProcessing;
                    media.url = result.data.url;
                    media.embedUrl = result.data.embedUrl;
                    media.height = result.data.height;
                    media.width = result.data.width;
                    abstract.media = allMedia.toArray();
                    this.setState({ ...this.state, abstract }, () => {
                        if (this._composerForm instanceof UploadsForm)
                            (this._composerForm as UploadsForm).updateMediaForm(media);
                    });
                }
            }
        }
        else {
            // this.setState({ ...this.state, loadingType: false }, () => {
            //     this.setErrors(M.Result.Result.errorsOrMessage(result), `Failed to load abstract media ${id}`);
            // });
        }
    }

    private async loadUserStats(eventId: number) {
        const result = await this.props.api.get(Api.endpoints.abstract.userStats.withUrlParams({ eventId }));
        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.setState({ ...this.state, userStats: result.data });
        }
        else {
            this.setState({ ...this.state, loadingAbstract: false }, () => {
                this.setErrors(M.Result.Result.errorsOrMessage(result), "Failed to load user stats");
            });
        }
    }

    private async onAbstractSavedHandler(abstract: M.Abstract.Abstract, isSubmitLater: boolean) {
        const guid = this.getGuid();
        const isNew = guid !== abstract.guid;

        if (isNew) {
            const eventId = this.getEventId();
            const step = this.getComposeStep();
            const url = Paths.AbstractsCompose(eventId, step, abstract.guid);
            this.props.replace(url);
            this.setState({ abstract });
            await this.loadAbstractType(abstract.guid);
            await new Promise((resolve) => setTimeout(() => resolve(), 25));
        }

        if (!this._isMounted)
            return;

        // need to reload abstract type id if different
        if (this.state.type && this.state.type.id !== abstract.typeId)
            await this.loadAbstractType(abstract.guid);

        if (!this._isMounted)
            return;

        await this.loadAbstract(abstract.guid);

        if (!this._isMounted)
            return;

        if (this._composerForm)
            this._composerForm.toggleProcessing(false);

        if (isSubmitLater) {
            this.props.notifySuccess("Abstract has been successfully saved for later.");
            this.props.goto(Paths.User.Abstracts.UserAbstracts);
        }
        else {
            await new Promise((resolve) => setTimeout(() => resolve(), 25));
            this.gotoNextStep(abstract.guid);
        }
    }

    private gotoPreviousStep() {
        const step = this.getComposeStep() || "authorship";
        const prevStep = this.getPreviousStep(step);
        if (prevStep)
            this.updateStep(prevStep);
    }

    private gotoNextStep(guid?: string) {
        const abstract = this.state.abstract;
        const step = this.getComposeStep() || "authorship";
        let nextStep = this.getNextStep(step);
        const editsAllowed = abstract && abstract.editsAllowed;
        const uploadsAllowed = abstract && abstract.uploadsAllowed;

        console.log("gotoNextStep", step, nextStep);
        console.log("gotoNextStep; e:", editsAllowed, "u:", uploadsAllowed);

        // if submit deadline has passed and we're submitting on uploads step,
        // go directly to abstract details page
        if (!editsAllowed && !uploadsAllowed) {
            this.props.goto(Paths.UserAbstractDetails(guid || this.getGuid()));
            return;
        }
        else if (step === "uploads" && !editsAllowed) {
            this.props.goto(Paths.UserAbstractDetails(guid || this.getGuid()));
            this.props.notifySuccess("Uploads have been saved");
            return;
        }
        else if (nextStep === "uploads" && !uploadsAllowed) {
            nextStep = "review";
        }

        if (nextStep) {
            this.updateStep(nextStep, false, guid);
        }
        else
            this.props.goto(Paths.AbstractsConfirmation(guid || this.getGuid()));
    }

    private updateAbstractMedia(media: M.Abstract.AbstractMedia) {
        const abstract = this.state.abstract;
        if (abstract) {
            let allMedia = abstract ? List(abstract.media) : List([]);
            const idx = allMedia.findIndex((m: M.Abstract.AbstractMedia) => m.id === media.id);

            if (idx === -1)
                allMedia = allMedia.push(media);
            else
                allMedia.set(idx, media);

            abstract.media = allMedia.toArray();
            this.setState({ ...this.state, abstract });
        }
    }

    private updateProcessedMedia(abstract: M.Abstract.Abstract) {
        // media finished processing
        const mediaIds = abstract.media.filter((m) => m.isProcessing).map((m) => ({ mediaId: m.id, abstractMediaId: m.abstractMediaId }));
        for (const x of mediaIds) {
            const processing = this.props.mediaProcessing[x.mediaId];
            if (processing !== undefined && !processing)
                this.loadAbstractMedia(x.abstractMediaId);
        }
    }

    private updateStep(nextStep: ComposeStep, isReplace: boolean = false, guid?: string) {
        const eventId = this.getEventId();

        if (!guid)
            guid = this.getGuid();

        const url = Paths.AbstractsCompose(eventId, nextStep, guid || "new")
            + this.props.location.search
            + this.props.location.hash;

        console.log("updateStep", isReplace, url);

        if (isReplace)
            this.props.replace(url, nextStep);
        else
            this.props.goto(url, nextStep);
    }
}

export const Composer: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ComposerComponent as any) as any);



// WEBPACK FOOTER //
// ./src/containers/abstracts/composer/Composer.tsx